import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BookmarkIcon, CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, GripVerticalIcon, LayersIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { newBlock, saveLanding, setLandingStatus } from '../../services/landingService';
import { newId } from '../../services/authService';
import { deleteComponent, instantiateComponent, saveComponent } from '../../services/componentService';
import { BLOCK_LIBRARY, blockDefinition } from '../../data/blockLibrary';
import { FONT_OPTIONS } from '../../data/fonts';
import { LandingBlock, LandingPage, LandingTheme } from '../../types';
import { BlockSettings } from '../../components/landings/BlockSettings';
import { StyleSettings } from '../../components/landings/StyleSettings';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { LandingRenderer } from '../../components/public/LandingRenderer';
import { ThankYouView } from '../../components/public/ThankYouView';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, landingUrl } from '../../utils/clipboard';
const THEMES: {
  value: LandingTheme;
  label: string;
}[] = [{
  value: 'blanco_premium',
  label: 'Blanco premium'
}, {
  value: 'claro_corporativo',
  label: 'Claro corporativo'
}, {
  value: 'minimalista',
  label: 'Minimalista'
}];
export function LandingEditor() {
  const {
    id = ''
  } = useParams();
  const {
    organization
  } = useAuth();
  const {
    landings,
    forms,
    components
  } = useWorkspace();
  const stored = landings.find(l => l.id === id);
  const [draft, setDraft] = useState<LandingPage | null>(stored ?? null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  /** Solo se arrastra desde el asa, para no mover bloques por accidente. */
  const [handleId, setHandleId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  /** Pestaña activa: con «gracias» la vista previa muestra la página de gracias. */
  const [tab, setTab] = useState('bloques');
  const [saveComponentFor, setSaveComponentFor] = useState<LandingBlock | null>(null);
  const [componentName, setComponentName] = useState('');
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  if (!draft) {
    return <div className="rounded-2xl border border-border bg-background p-10 text-center" data-id="element-964" data-mp="36z1n3qo97i9g">
        <p className="text-sm text-muted-foreground" data-id="element-965" data-mp="3bye5nz83e368">Esta landing no existe.</p>
        <Link to="/app/landings" className="mt-4 inline-block" data-id="element-966" data-mp="3bwa4hp1kvi9n">
          <Button variant="secondary" data-id="element-967" data-mp="3gw6atuxqzlsd">Volver a landings</Button>
        </Link>
      </div>;
  }
  const form = forms.find(f => f.id === draft.formId);
  const blocks = draft.pageConfig.blocks;
  const selectedBlock = blocks.find(b => b.id === selectedBlockId) ?? null;
  function update(partial: Partial<LandingPage>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  function updateConfig(partial: Partial<LandingPage['pageConfig']>) {
    update({
      pageConfig: {
        ...draft!.pageConfig,
        ...partial
      }
    });
  }
  function updateBlock(block: LandingBlock) {
    updateConfig({
      blocks: blocks.map(b => b.id === block.id ? block : b)
    });
  }
  function addBlock(block: LandingBlock) {
    updateConfig({
      blocks: [...blocks, block]
    });
    setSelectedBlockId(block.id);
    setAddOpen(false);
  }
  function duplicateBlock(block: LandingBlock) {
    const copy: LandingBlock = {
      ...(JSON.parse(JSON.stringify(block)) as LandingBlock),
      // El id debe ser un UUID válido: `landing_blocks.id` es de tipo uuid.
      id: newId('block')
    };
    const index = blocks.findIndex(b => b.id === block.id);
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    updateConfig({
      blocks: next
    });
  }
  /**
   * Reordena solo en vertical: se mueve el objeto completo del bloque, así que
   * conserva id, type, config, style, contenido y visibilidad.
   */
  function reorder(targetId: string) {
    setDragOverId(null);
    setHandleId(null);
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const next = [...blocks];
    const fromIndex = next.findIndex(b => b.id === dragId);
    const toIndex = next.findIndex(b => b.id === targetId);
    next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
    updateConfig({
      blocks: next
    });
    setDragId(null);
  }
  async function handleSave(status?: LandingPage['status']) {
    setSaving(true);
    setSaveError(null);
    try {
      await saveLanding(draft!);
      if (status) {
        await setLandingStatus(draft!.id, status);
        update({
          status
        });
      }
      setSaved(true);
      if (status === 'publicada') {
        toast.success('Landing publicada correctamente', {
          description: landingUrl(draft!.slug)
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido.';
      setSaveError(message);
      toast.error('No se ha podido guardar', {
        description: message
      });
    } finally {
      setSaving(false);
    }
  }
  /** Copia la URL pública, con confirmación visible. */
  async function handleCopyLink() {
    const ok = await copyToClipboard(landingUrl(draft!.slug));
    if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace', {
      description: landingUrl(draft!.slug)
    });
  }
  async function confirmSaveComponent() {
    if (!saveComponentFor || !organization) return;
    await saveComponent(organization.id, componentName || blockDefinition(saveComponentFor.type).label, blockDefinition(saveComponentFor.type).label, saveComponentFor);
    setSaveComponentFor(null);
    setComponentName('');
  }
  return <>
      <Link to="/app/landings" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-968" data-mp="19g4vjfzq4pwy">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-969" data-mp="14kphoo1puu0j" />
        Volver a landings
      </Link>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-970" data-mp="ugskjdlpo5bt">
        <div data-id="element-971" data-mp="zfc8tkns1t7c">
          <h1 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl" data-id="element-972" data-mp="14cm4hi1rf13m">
            {draft.name}
          </h1>
          <p className="text-sm text-muted-foreground" data-id="element-973" data-mp="19byv8dvr4sbh">
            /landing/{draft.slug} ·{' '}
            {draft.status === 'publicada' ? 'Publicada' : draft.status === 'borrador' ? 'Borrador' : 'Despublicada'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-974" data-mp="gnb25w4rgynd">
          {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-975" data-mp="llunpvfg08f7">
              Guardando…
            </span> : saved ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-976" data-mp="qdmjqlhdb6cq">
              <CheckIcon className="h-4 w-4" data-id="element-977" data-mp="3p1s4in840emi" /> Guardado correctamente
            </span> : <span className="self-center text-sm text-muted-foreground" data-id="element-978" data-mp="3u1edgkgwn8pi">
              Cambios sin guardar
            </span>}
          <Button variant="ghost" onClick={() => handleSave('borrador')} loading={saving} data-id="element-979" data-mp="1c3rwu9ke5vvd">
            Guardar borrador
          </Button>
          <Button variant="secondary" leftIcon={<EyeIcon className="h-4 w-4" data-id="element-981" data-mp="1m1pm90dzlypk" />} onClick={() => window.open(`/landing/${draft.slug}`, '_blank')} data-id="element-980" data-mp="183risq17fmou">
            Vista previa
          </Button>
          <Button variant="secondary" leftIcon={<CopyIcon className="h-4 w-4" data-id="element-983" data-mp="1v1cmhiuzcyh1" />} onClick={handleCopyLink} data-id="element-982" data-mp="1h465nttth9ug">
            Copiar enlace
          </Button>
          <Button onClick={() => handleSave('publicada')} loading={saving} data-id="element-984" data-mp="1r12gw7b3oa2p">
            {draft.status === 'publicada' ? 'Actualizar publicación' : 'Publicar'}
          </Button>
        </div>
      </div>

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-985" data-mp="1ztvy4aa3xjj7">
          <span data-id="element-986" data-mp="29rtn4zzsjkcv">
            <strong className="font-semibold" data-id="element-987" data-mp="29sbryl0ir6we">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={() => handleSave()} loading={saving} data-id="element-988" data-mp="2tdbxsnluxcg9">
            Reintentar
          </Button>
        </div>}

      <div className="grid items-start gap-5 xl:grid-cols-[380px_minmax(0,1fr)]" data-id="element-989" data-mp="2jemq1nnod8dq">
        {/* Panel izquierdo, con scroll propio para no perder la vista previa */}
        <div className="min-w-0 xl:max-h-[860px] xl:overflow-y-auto xl:pr-1" data-id="element-990" data-mp="2ofk8808oq2f8">
          <Tabs defaultValue="bloques" value={tab} onValueChange={setTab} data-id="element-991" data-mp="29n08l9kqud2j">
            <TabsList data-id="element-992" data-mp="2efjoid6wlkai">
              <TabsTrigger value="bloques" data-id="element-993" data-mp="2egup6451ybxe">Bloques</TabsTrigger>
              <TabsTrigger value="diseno" data-id="element-994" data-mp="20oeqffj6p02a">Diseño</TabsTrigger>
              <TabsTrigger value="gracias" data-id="element-995" data-mp="20m21gif0dpfq">Gracias</TabsTrigger>
            </TabsList>

            <TabsContent value="bloques" data-id="element-996" data-mp="25n7zmvrfu4wn">
              <p className="mb-3 text-xs text-muted-foreground" data-id="element-997" data-mp="1qp75o7a4e355">
                Haz clic en cualquier sección de la vista previa para editarla, o
                arrastra los bloques para reordenarlos.
              </p>
              <ul className="space-y-2" data-id="element-998" data-mp="u6ycjkpsgs89">
                {blocks.map(block => {
                const definition = blockDefinition(block.type);
                const active = selectedBlockId === block.id;
                return <li key={block.id} draggable={handleId === block.id} onDragStart={e => {
                  setDragId(block.id);
                  e.dataTransfer.effectAllowed = 'move';
                }} onDragEnd={() => {
                  setDragId(null);
                  setDragOverId(null);
                  setHandleId(null);
                }} onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragId && dragId !== block.id) setDragOverId(block.id);
                }} onDragLeave={() => setDragOverId(prev => prev === block.id ? null : prev)} onDrop={() => reorder(block.id)} className={`relative rounded-xl border bg-background shadow-sm transition-colors ${active ? 'border-primary' : 'border-border'} ${dragId === block.id ? 'opacity-50' : ''} ${dragOverId === block.id ? 'before:absolute before:-top-1 before:left-0 before:h-0.5 before:w-full before:rounded-full before:bg-primary' : ''}`} data-id="element-999" data-mp="kb3na6r27ywi">
                      <div className="flex items-center gap-1 p-2.5" data-id="element-1000" data-mp="3v2z2wsxp9mnt">
                        <span onPointerDown={() => setHandleId(block.id)} onPointerUp={() => setHandleId(null)} className="flex h-8 w-6 flex-none cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing" aria-hidden="true" data-id="element-1001" data-mp="3q4ntukf8bg6n">
                          <GripVerticalIcon className="h-4 w-4" data-id="element-1002" data-mp="2xicvlgf3hrd0" />
                        </span>
                        <button type="button" onClick={() => setSelectedBlockId(active ? null : block.id)} className="min-w-0 flex-1 text-left" data-id="element-1003" data-mp="3bhm9uajqqbec">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1004" data-mp="qpj13s03fjfo">
                            {definition.label}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground" data-id="element-1005" data-mp="3fixpa7j0xa78">
                            {definition.description}
                          </span>
                        </button>
                        <button type="button" aria-label="Guardar en la biblioteca" onClick={() => {
                      setSaveComponentFor(block);
                      setComponentName(definition.label);
                    }} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1006" data-mp="3ph516bygh22d">
                          <BookmarkIcon className="h-4 w-4" data-id="element-1007" data-mp="31ras85h9bxne" />
                        </button>
                        <button type="button" aria-label={block.visible ? 'Ocultar bloque' : 'Mostrar bloque'} onClick={() => updateBlock({
                      ...block,
                      visible: !block.visible
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1008" data-mp="35t5txb33od1p">
                          {block.visible ? <EyeIcon className="h-4 w-4" data-id="element-1009" data-mp="2i2rydalb3w2d" /> : <EyeOffIcon className="h-4 w-4" data-id="element-1010" data-mp="2i20datpc5gqc" />}
                        </button>
                        <button type="button" aria-label="Duplicar bloque" onClick={() => duplicateBlock(block)} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1011" data-mp="2ajin1bt8pnao">
                          <CopyIcon className="h-4 w-4" data-id="element-1012" data-mp="21ib2dwjnckz2" />
                        </button>
                        {definition.removable && <button type="button" aria-label="Eliminar bloque" onClick={() => updateConfig({
                      blocks: blocks.filter(b => b.id !== block.id)
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-1013" data-mp="1wjhpo6c6oxzy">
                            <Trash2Icon className="h-4 w-4" data-id="element-1014" data-mp="1hwepfrwh8b8x" />
                          </button>}
                      </div>

                      {active && <div className="border-t border-border p-4" data-id="element-1015" data-mp="1droty45qcyoj">
                          <Tabs defaultValue="contenido" data-id="element-1016" data-mp="18tdkhujckq6u">
                            <TabsList data-id="element-1017" data-mp="13vvlavv4tpc1">
                              <TabsTrigger value="contenido" data-id="element-1018" data-mp="13wwwr1g28dua">Contenido</TabsTrigger>
                              <TabsTrigger value="estilo" data-id="element-1019" data-mp="33xlfb8bz9djl">Estilo</TabsTrigger>
                            </TabsList>
                            <TabsContent value="contenido" data-id="element-1020" data-mp="38vmwfnpuf2sf">
                              <BlockSettings block={block} onChange={updateBlock} data-id="element-1021" data-mp="2u8bhdbugd0nk" />
                            </TabsContent>
                            <TabsContent value="estilo" data-id="element-1022" data-mp="2z63b8benqwue">
                              <StyleSettings block={block} onChange={updateBlock} data-id="element-1023" data-mp="2l7dcysg3qr4g" />
                            </TabsContent>
                          </Tabs>
                        </div>}
                    </li>;
              })}
              </ul>
              <Button className="mt-4 w-full" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1025" data-mp="2g897ocwatdje" />} onClick={() => setAddOpen(true)} data-id="element-1024" data-mp="2ba7m9lbeh24r">
                Añadir sección
              </Button>
            </TabsContent>

            <TabsContent value="diseno" data-id="element-1026" data-mp="9im6f9wvdz05">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1027" data-mp="egnpvvy7k0c5">
                <Input id="l-name" label="Nombre interno" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-1028" data-mp="1yyya7rvfvwff" />
                <Input id="l-slug" label="Slug" helperText={`${window.location.origin}/landing/${draft.slug}`} value={draft.slug} onChange={e => update({
                slug: e.target.value
              })} data-id="element-1029" data-mp="1u1ev6pqx3fmf" />
                <Select id="l-form" label="Formulario asociado" placeholder="Sin formulario" options={forms.map(f => ({
                value: f.id,
                label: f.name
              }))} value={draft.formId ?? ''} onChange={e => update({
                formId: e.target.value || undefined
              })} data-id="element-1030" data-mp="23wq6sz7146py" />
                <Select id="l-theme" label="Tema visual" options={THEMES} value={draft.pageConfig.theme} onChange={e => updateConfig({
                theme: e.target.value as LandingTheme
              })} data-id="element-1031" data-mp="2moj435t4se3h" />
                <Input id="l-color" label="Color principal" type="color" value={draft.pageConfig.primaryColor} onChange={e => updateConfig({
                primaryColor: e.target.value
              })} data-id="element-1032" data-mp="sarkpel02fuu" />
                <Select id="l-font" label="Tipografía" options={FONT_OPTIONS} value={draft.pageConfig.fontFamily} onChange={e => updateConfig({
                fontFamily: e.target.value
              })} data-id="element-1033" data-mp="1lzz5b9jql2ii" />
                <div data-id="element-1034" data-mp="1vyy3fy6vlfr9">
                  <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1035" data-mp="1vz7snjk3jivw">
                    Logotipo de la landing
                  </p>
                  <LogoUploader value={draft.pageConfig.logoUrl} onChange={value => updateConfig({
                  logoUrl: value
                })} data-id="element-1036" data-mp="20vos39wqxjoa" />
                  <p className="mt-2 text-xs text-muted-foreground" data-id="element-1037" data-mp="29quqhntoszgs">
                    Si lo dejas vacío se usa el logotipo de tu empresa.
                  </p>
                </div>
              </div>

              {/* Biblioteca de componentes */}
              <div className="mt-5 rounded-2xl border border-border bg-background p-5" data-id="element-1038" data-mp="2jm68qg4b0ota">
                <div className="mb-3 flex items-center gap-2" data-id="element-1039" data-mp="2okzhjv8nbrn4">
                  <LayersIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1040" data-mp="d0i7mmet9if4" />
                  <h3 className="text-sm font-semibold text-foreground" data-id="element-1041" data-mp="80dzpkryxw0i">
                    Biblioteca de componentes
                  </h3>
                </div>
                {components.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-1042" data-mp="3u8nfcpc2yld7">
                    Guarda cualquier bloque con el icono de marcador para reutilizarlo
                    en otras landings.
                  </p> : <ul className="space-y-2" data-id="element-1043" data-mp="qqlrx7u4r21x">
                    {components.map(component => <li key={component.id} className="flex items-center gap-2 rounded-xl border border-border p-3" data-id="element-1044" data-mp="m0f43a7fzlkq">
                        <span className="min-w-0 flex-1" data-id="element-1045" data-mp="h1vft35dlxp7">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1046" data-mp="gz9ala5yjua5">
                            {component.name}
                          </span>
                          <span className="block text-xs text-muted-foreground" data-id="element-1047" data-mp="19p7u6q1duz7f">
                            {component.category}
                          </span>
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => addBlock(instantiateComponent(component))} data-id="element-1048" data-mp="mty7l9mj34m2">
                          Insertar
                        </Button>
                        <button type="button" aria-label="Eliminar componente" onClick={() => deleteComponent(component.id)} className="rounded-md p-2 text-muted-foreground hover:text-red-600" data-id="element-1049" data-mp="e1w9vj3ug2fh">
                          <Trash2Icon className="h-4 w-4" data-id="element-1050" data-mp="15sae0nm220dy" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </TabsContent>

            <TabsContent value="gracias" data-id="element-1051" data-mp="wqt2zjni8ow4">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1052" data-mp="wprg8hx4mdyk">
                <Input id="t-title" label="Título" value={draft.pageConfig.thankYou.title} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  title: e.target.value
                }
              })} data-id="element-1053" data-mp="3cgvswpz0p606" />
                <Input id="t-subtitle" label="Subtítulo" value={draft.pageConfig.thankYou.subtitle ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  subtitle: e.target.value
                }
              })} data-id="element-1054" data-mp="34zqzid1opwrr" />
                <Textarea id="t-text" label="Texto" rows={3} value={draft.pageConfig.thankYou.text} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  text: e.target.value
                }
              })} data-id="element-1055" data-mp="2q99wwqdbd0ta" />
                <Switch id="t-video" label="Mostrar vídeo" checked={Boolean(draft.pageConfig.thankYou.showVideo)} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showVideo: checked
                }
              })} data-id="element-1056" data-mp="2v9edshi4vxk1" />
                <Input id="t-videourl" label="URL del vídeo (YouTube, Vimeo, Wistia o mp4)" value={draft.pageConfig.thankYou.videoUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoUrl: e.target.value
                }
              })} data-id="element-1057" data-mp="z015mvt81ntu" />
                <Input id="t-videothumb" label="Miniatura del vídeo (URL)" value={draft.pageConfig.thankYou.videoThumbnailUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoThumbnailUrl: e.target.value
                }
              })} data-id="element-1058" data-mp="13ycgn9v964dn" />
                <Input id="t-image" label="Imagen (URL)" value={draft.pageConfig.thankYou.imageUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  imageUrl: e.target.value
                }
              })} data-id="element-1059" data-mp="1ro6sbo3u5mwb" />
                <Switch id="t-cal" label="Mostrar botón de agendar llamada" checked={draft.pageConfig.thankYou.showCalendly} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showCalendly: checked
                }
              })} data-id="element-1060" data-mp="3oj2xelp8rrzg" />
                <Input id="t-calurl" label="URL de Calendly" value={draft.pageConfig.thankYou.calendlyUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  calendlyUrl: e.target.value
                }
              })} data-id="element-1061" data-mp="1u7bl7rbfoqv5" />
                <Switch id="t-wa" label="Mostrar botón de WhatsApp" checked={draft.pageConfig.thankYou.showWhatsapp} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showWhatsapp: checked
                }
              })} data-id="element-1062" data-mp="1q5g9rtzsgx3n" />
                <Input id="t-wanum" label="Número de WhatsApp" value={draft.pageConfig.thankYou.whatsappNumber ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  whatsappNumber: e.target.value
                }
              })} data-id="element-1063" data-mp="35mqdwiwm0wkg" />
                <Switch id="t-home" label="Mostrar botón de volver al inicio" checked={draft.pageConfig.thankYou.showHome} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showHome: checked
                }
              })} data-id="element-1064" data-mp="2z81erndh7t1x" />
                <Input id="t-redirect" label="Redirección automática (URL)" value={draft.pageConfig.thankYou.autoRedirectUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  autoRedirectUrl: e.target.value
                }
              })} data-id="element-1065" data-mp="3mxdl8fhf5n0j" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Vista previa editable, fija mientras el panel izquierdo se desplaza */}
        <div className="h-[860px] min-w-0 xl:sticky xl:top-4" data-id="element-1066" data-mp="1se3ndyec1voi">
          <DevicePreview device={device} onDeviceChange={setDevice} toolbar={tab === 'gracias' ? <span className="text-xs text-muted-foreground" data-id="element-1068" data-mp="1xdzsy22ohv66">
                  Vista previa de la página de gracias
                </span> : selectedBlock ? <span className="text-xs text-muted-foreground" data-id="element-1069" data-mp="26dv80hzcuabh">
                  Editando: {blockDefinition(selectedBlock.type).label}
                </span> : <span className="text-xs text-muted-foreground" data-id="element-1070" data-mp="2bc6fwn63aar2">
                  Haz clic en una sección para editarla
                </span>} data-id="element-1067" data-mp="1sfy759dvbhr0">
            {tab === 'gracias' ? <ThankYouView config={draft.pageConfig.thankYou} logoUrl={draft.pageConfig.logoUrl ?? organization?.brand.logoUrl ?? organization?.logoUrl} primaryColor={draft.pageConfig.primaryColor} preview data-id="element-1071" data-mp="2l314hy6t5mfo" /> : <LandingRenderer landing={draft} form={form} preview selectedBlockId={selectedBlockId} onSelectBlock={setSelectedBlockId} viewport={device === 'mobile' || device === 'iphone16' ? 'mobile' : device === 'tablet' ? 'tablet' : 'desktop'} data-id="element-1072" data-mp="2u3pgyogjzeqg" />}
          </DevicePreview>
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Añadir sección" data-id="element-1073" data-mp="2gh06x1xnz6zw">
        {components.length > 0 && <div className="mb-6" data-id="element-1074" data-mp="2v663sj914l4m">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1075" data-mp="2v53g0k706ysx">
              Mis componentes
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1076" data-mp="22gpgg009nuf9">
              {components.map(component => <li key={component.id} data-id="element-1077" data-mp="22ise967nuruj">
                  <button type="button" onClick={() => addBlock(instantiateComponent(component))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1078" data-mp="1xinu967jnezk">
                    <span className="block text-sm font-medium text-foreground" data-id="element-1079" data-mp="27eh76qp6dahv">
                      {component.name}
                    </span>
                    <span className="block text-xs text-muted-foreground" data-id="element-1080" data-mp="3iv7fqgh2meen">
                      {component.category}
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1081" data-mp="3ve35ut4m6bh9">
          Secciones disponibles
        </p>
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1082" data-mp="3hfuzjulotxtf">
          {BLOCK_LIBRARY.map(definition => <li key={definition.type} data-id="element-1083" data-mp="3lg5ie7ukwipp">
              <button type="button" onClick={() => addBlock(newBlock(definition.type))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1084" data-mp="cztwo6vlxiol">
                <span className="block text-sm font-medium text-foreground" data-id="element-1085" data-mp="47rxebx9y8fw">
                  {definition.label}
                </span>
                <span className="block text-xs text-muted-foreground" data-id="element-1086" data-mp="87soox5h2e4d">
                  {definition.description}
                </span>
              </button>
            </li>)}
        </ul>
      </Modal>

      <Modal open={Boolean(saveComponentFor)} onClose={() => setSaveComponentFor(null)} title="Guardar en la biblioteca" footer={<>
            <Button variant="ghost" onClick={() => setSaveComponentFor(null)} data-id="element-1088" data-mp="1aj28f6vylzvr">
              Cancelar
            </Button>
            <Button onClick={confirmSaveComponent} data-id="element-1089" data-mp="11kgiurs2lklh">Guardar componente</Button>
          </>} data-id="element-1087" data-mp="10q7s87dehznx">
        <Input id="component-name" label="Nombre del componente" value={componentName} onChange={e => setComponentName(e.target.value)} data-id="element-1090" data-mp="wllznoq6xu0u" />
        <p className="mt-3 text-sm text-muted-foreground" data-id="element-1091" data-mp="rnkg72oursou">
          Se guardará con su contenido y su estilo actual para que puedas reutilizarlo
          en cualquier otra landing de tu empresa.
        </p>
      </Modal>
    </>;
}